// react
import { MouseEvent, MouseEventHandler, ReactNode, useContext, useState } from 'react';

// types
import { userRolesWbg } from '../../../@types/unions';
import { UserRoleWbg } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';
import { supportedLocales } from '../../../i18n';

// router
import { NavLink, Link as RouterLink } from 'react-router-dom';

// utils
import { translateLanguage } from '../../../utils/translations';

// hooks
import { useLearningHub } from '../../../hooks/useLearningHub';

// mui
import {
	Box,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';

// ui
import { Flag, FlagProps } from '../Flag/Flag';
import { Loading } from '../Loading/Loading';

// icons
import {
	AutoStoriesOutlined as IconAutoStories,
	ChevronLeft as IconChevronLeft,
	ChevronRight as IconChevronRight,
	ClearOutlined as IconClear,
	DashboardOutlined as IconDashboard,
	FolderOutlined as IconFolder,
	NewReleasesOutlined as IconNewFeatures,
	TranslateOutlined as IconTranslate,
} from '@mui/icons-material';

// img
import { LogoFundsChainAlt } from '../../img/LogoFundsChainAlt';
import { LogoFundsChainMark } from '../../img/LogoFundsChainMark';

// context
import { ContextUser } from '../../../App.context';

// config
import { featureFlags } from '../../../config/featureFlags';

// theme
import { theme } from '../../../theme';

// props
export interface NavItem {
	className?: string;
	icon: ReactNode;
	id?: string;
	label: string;
	onClick?: MouseEventHandler;
	target?: string;
	to?: string;
}

export interface NavProps {
	isOpen?: boolean;
	onClose?: MouseEventHandler;
}

export const Nav = ({ isOpen, onClose }: NavProps) => {
	// hooks
	const { handleClickLearningHub, statusGetLearningHub } = useLearningHub();
	const { i18n, t } = useTranslation();

	// state
	const [anchorLang, setAnchorLang] = useState<null | HTMLElement>(null);
	const [isExpanded, setIsExpanded] = useState(false);

	// context
	const { user } = useContext(ContextUser);

	const handleClickNavItem = (e: MouseEvent<HTMLButtonElement>) => {
		setIsExpanded(false);

		if (onClose) {
			onClose(e);
		}
	};

	const handleClickLang = (e: MouseEvent<HTMLButtonElement>) => {
		setAnchorLang(e.currentTarget);
	};

	const handleChangeLang = (e: MouseEvent<HTMLLIElement>, lang: string) => {
		// change lang
		i18n.changeLanguage(lang);
		sessionStorage.setItem('language', lang);

		// close lang menu
		setAnchorLang(null);

		// close nav
		if (onClose) {
			onClose(e);
		}
	};

	const items = [];

	// add dashboard
	if (user && user.role && userRolesWbg.includes(user.role as UserRoleWbg)) {
		items.push({
			icon: <IconDashboard />,
			label: t('dashboard'),
			to: '/dashboard',
		});
	}

	items.push({
		icon: <IconFolder />,
		label: t('projects'),
		to: '/projects',
	});

	const itemsFooter: Array<NavItem> = [
		{
			id: 'btn-toggle',
			icon: isExpanded ? <IconChevronLeft /> : <IconChevronRight />,
			label: isExpanded ? t('collapse') : t('expand'),
			onClick: () => {
				setIsExpanded(!isExpanded);
			},
		},
	];

	itemsFooter.push({
		icon: <IconNewFeatures />,
		label: t('newFeatures'),
		to: '/features',
	});

	if (featureFlags['LEARNING_HUB']) {
		itemsFooter.push({
			icon:
				statusGetLearningHub === 'LOADING' ? (
					<Loading CircularProgressProps={{ color: 'inherit' }} size="xs" />
				) : (
					<IconAutoStories />
				),
			id: 'btn-learning-hub',
			label: t(`learningHub`),
			onClick: handleClickLearningHub,
		});
	}

	itemsFooter.push({
		icon: <IconTranslate />,
		id: 'btn-language',
		label: t('language'),
		onClick: handleClickLang,
	});

	const renderItems = (items: NavItem[]) => {
		return items.map((item, i) => {
			const { icon, label, id, ...itemProps } = item;

			const isBtnToggle = id === 'btn-toggle';

			const btn = (
				<Box
					key={`nav-item-${i}`}
					component={item.to ? NavLink : 'button'}
					onClick={(e: MouseEvent<HTMLButtonElement>) => handleClickNavItem(e)}
					sx={(theme: Theme) => ({
						alignItems: 'center',
						borderRadius: '8px',
						color: theme.palette.common.white,
						display: isBtnToggle ? 'none' : 'flex',
						gap: theme.spacing(1),
						justifyContent: 'flex-start',
						padding: theme.spacing(1),
						textDecoration: 'none',
						transition: 'all 200ms',
						width: '100%',
						'&:hover': {
							backgroundColor: theme.palette.brand.blue[600],
						},
						'&.active': {
							backgroundColor: theme.palette.brand.blue[600],
						},
						'& svg': {
							fill: theme.palette.brand.blue[400],
							transition: 'fill 200ms',
							[theme.breakpoints.up('lg')]: {
								fill: theme.palette.common.white,
							},
						},
						[theme.breakpoints.up('lg')]: {
							display: isBtnToggle ? 'inline-flex' : 'flex',
							justifyContent: isExpanded ? 'flex-start' : 'center',
						},
					})}
					{...itemProps}>
					{icon}
					<Typography
						component="div"
						fontWeight={600}
						sx={(theme: Theme) => ({
							display: 'inline-flex',
							[theme.breakpoints.up('lg')]: {
								display: isExpanded ? 'inline-flex' : 'none',
							},
						})}>
						{label}
					</Typography>
				</Box>
			);

			if (!isExpanded) {
				return (
					<Tooltip arrow={true} key={`nav-item-tooltip-${i}`} placement="right" title={label}>
						{btn}
					</Tooltip>
				);
			}

			return btn;
		});
	};

	let backgroundColor = theme.palette.brand.blue[800];

	if (process.env.REACT_APP_ENV) {
		if (['development', 'local', 'qa'].includes(process.env.REACT_APP_ENV)) {
			backgroundColor = theme.palette.brand.blue[700];
		}
	}

	return (
		<Box sx={{ position: 'relative' }}>
			<Box
				onClick={() => setIsExpanded(false)}
				sx={{
					backgroundColor: 'rgba(0,0,0,0.2)',
					height: '100vh',
					left: 0,
					opacity: isExpanded ? 1 : 0,
					pointerEvents: isExpanded ? 'all' : 'none',
					position: 'fixed',
					top: 0,
					transition: 'all 200ms',
					width: '100vw',
				}}
			/>
			<Box
				sx={(theme: Theme) => ({
					alignItems: 'center',
					backgroundColor,
					boxShadow: 0,
					display: 'flex',
					flexDirection: 'column',
					height: '100vh',
					left: 0,
					overflowY: 'auto',
					padding: theme.spacing(2),
					position: 'fixed',
					top: 0,
					transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
					transition: 'all 200ms',
					width: '100%',
					[theme.breakpoints.up('md')]: {
						padding: theme.spacing(4),
					},
					[theme.breakpoints.up('lg')]: {
						alignItems: isExpanded ? 'flex-start' : 'center',
						boxShadow: isExpanded ? theme.shadows[16] : 0,
						padding: theme.spacing(0, 1),
						transform: 'translateX(0)',
						width: isExpanded
							? theme.variables?.navWidthExpanded
							: theme.variables?.navWidthCollapsed,
					},
				})}>
				<Box
					component={RouterLink}
					to="/"
					sx={(theme: Theme) => ({
						alignItems: 'center',
						color: theme.palette.common.white,
						display: 'flex',
						flexDirection: 'column',
						gap: theme.spacing(1),
						height: 'auto',
						justifyContent: 'flex-start',
						padding: theme.spacing(1, 0, 0, 1),
						textDecoration: 'none',
						width: 'auto',
						[theme.breakpoints.up('lg')]: {
							alignItems: 'center',
							flexDirection: 'row',
							height: 70,
							justifyContent: isExpanded ? 'flex-start' : 'center',
							padding: isExpanded ? theme.spacing(0, 1) : 0,
							width: '100%',
						},
					})}>
					{isExpanded ? (
						<Box sx={{ width: 140 }}>
							<LogoFundsChainAlt />
						</Box>
					) : (
						<Box
							sx={(theme: Theme) => ({
								color: theme.palette.common.white,
								height: 'auto',
								width: 40,
								[theme.breakpoints.up('lg')]: {
									display: 'inline-flex',
									width: '80%',
								},
							})}>
							<LogoFundsChainMark />
						</Box>
					)}
				</Box>

				<Box
					component="nav"
					sx={(theme: Theme) => ({
						display: 'flex',
						flex: 1,
						flexDirection: 'column',
						gap: theme.spacing(1),
						justifyContent: 'space-between',
						margin: theme.spacing(3, 0, 0, 0),
						width: '100%',
						[theme.breakpoints.up('lg')]: {
							padding: theme.spacing(0, 0, 1, 0),
						},
					})}>
					<Box
						sx={(theme: Theme) => ({
							display: 'flex',
							flexDirection: 'column',
							gap: theme.spacing(1),
						})}>
						{renderItems(items)}
					</Box>
					<Box
						sx={(theme: Theme) => ({
							display: 'flex',
							flexDirection: 'column',
							gap: theme.spacing(1),
						})}>
						{renderItems(itemsFooter)}
					</Box>

					<Menu
						anchorEl={anchorLang}
						id="menu-language"
						MenuListProps={{
							'aria-labelledby': 'btn-language',
						}}
						onClose={() => setAnchorLang(null)}
						open={Boolean(anchorLang)}>
						{supportedLocales.map((locale) => {
							const localeArray = locale.split('-');

							return (
								<MenuItem
									key={`lang-${localeArray[0]}`}
									onClick={(e) => {
										handleChangeLang(e, localeArray[0]);
									}}>
									<ListItemIcon>
										<Flag code={localeArray[1] as FlagProps['code']} />
									</ListItemIcon>
									<ListItemText>{translateLanguage(localeArray[0])}</ListItemText>
								</MenuItem>
							);
						})}
					</Menu>
				</Box>

				<Box
					sx={(theme: Theme) => ({
						position: 'absolute',
						right: theme.spacing(1),
						top: theme.spacing(1),
						[theme.breakpoints.up('lg')]: {
							display: 'none',
						},
					})}>
					<IconButton color="white" onClick={onClose}>
						<IconClear />
					</IconButton>
				</Box>
			</Box>
		</Box>
	);
};
